/**
 * The pending study-group invite — a pre-auth return target for the ONE surface
 * that legitimately has no user id to key on.
 *
 * WHY THIS EXISTS (board sYIAm69jLziJCcJifsMO, 2026-09-10)
 * --------------------------------------------------------
 * `GroupInviteLanding` is shown to a SIGNED-OUT visitor holding an invite link.
 * It has to remember `/community/groups/join/<code>` across the sign-up round
 * trip, and at the moment it writes there is no authenticated user — so it
 * cannot use `src/lib/last-visited-page.ts`, which is now keyed by uid.
 *
 * It previously wrote the last-visited key directly. That is what made the
 * last-visited store identity-less, and identity-less is the whole bug: a value
 * written by nobody in particular is inherited by whoever signs in next. The
 * two needs are genuinely different, so they now get two stores rather than one
 * store with a null-uid escape hatch — a null-uid entry that any user may claim
 * would reintroduce exactly the defect the uid keying removes.
 *
 * WHY sessionStorage AND NOT localStorage (code-reviewer, 2026-09-10, HIGH)
 * -------------------------------------------------------------------------
 * This store cannot be keyed by uid — there is no user yet, which is the whole
 * point of it. So the FIRST draft simply moved the identity-less value into its
 * own localStorage key, and that reproduced the original bug in miniature: an
 * invite the visitor ABANDONED (closed the tab, never signed up) survives in
 * that browser, is peeked for ANY uid, and `GroupInviteLanding` auto-joins
 * unconditionally (see its signed-in effect) — so the next person to sign in on
 * that machine within the TTL is silently joined to a stranger's private study
 * group. Narrower than the last-visited bug (a wrong destination) and worse in
 * kind (an unintended membership in someone else's group).
 *
 * `sessionStorage` is scoped to the TAB, which is the correct scope for this
 * value and needs no identity: the invitee clicks the link, signs up, and comes
 * back IN THE SAME TAB. A different person on the same machine is a different
 * tab (or a later session) and cannot see it at all. The browser enforces what
 * a uid comparison would otherwise have to, and abandonment is handled by the
 * tab closing rather than by a timer.
 *
 * The TTL is kept anyway, unchanged, as a second bound on a long-lived tab —
 * belt-and-braces, not the primary guard.
 *
 * WHY NOT `setPostAuthIntent` INSTEAD
 * ------------------------------------
 * `auth-intent.ts` validates `returnTo` against `RETURN_TO_ALLOWLIST`, a list of
 * exact string LITERALS, and that literal-ness is the property which makes it
 * safe (see its header: an attacker who controls storage can still only hand
 * back one of two fixed strings). An invite path carries a per-group code, so it
 * can never be a literal — accepting it would mean turning that allowlist into
 * a prefix match and weakening the guarantee for `/lp/nextgen-practice-a` and
 * every other paid funnel that depends on it. A separate, narrowly-scoped store
 * costs less than that.
 *
 * The prefix check below is NOT the same relaxation. It is a second, isolated
 * store whose only permitted value shape is one route, it is never consulted for
 * anything but the invite return, and it carries a TTL. `auth-intent.ts` keeps
 * its literal allowlist untouched.
 *
 * PEEK, DO NOT CONSUME, ON READ
 * ------------------------------
 * `getPostLoginRedirect()` runs inside effects that fire repeatedly (`AuthForm`
 * carries a loop counter for that reason, and `AuthPage` deliberately computes
 * the destination once and reuses it for its 500ms hard fallback). A read that
 * consumed would hand the second caller a different answer and send the hard
 * fallback somewhere other than where the router `navigate` aimed.
 * `GroupInviteLanding` clears it on arrival instead — the same
 * peek-here / consume-at-the-destination split `auth-intent.ts` uses.
 */

const PENDING_INVITE_KEY = 'jds_pending_invite';

/**
 * The only route this store may ever hand back.
 *
 * A trailing segment (the invite code) follows, so this is a prefix rather than
 * a literal — see the header for why that is acceptable here and not in
 * `auth-intent.ts`. It is anchored, same-origin by construction, and cannot
 * express `//`, `/\`, or an off-origin value.
 */
const INVITE_PATH_PREFIX = '/community/groups/join/';

/**
 * A stale invite must not outrank a later, deliberate destination forever. 24h
 * matches `auth-intent.ts` — long enough to survive an email round trip and a
 * night's sleep, short enough that a forgotten click does not redirect someone
 * a week later.
 */
const TTL_MS = 24 * 60 * 60 * 1000;

interface StoredPendingInvite {
  path: string;
  ts: number;
}

/**
 * Same reasoning as `last-visited-page.ts`: the value becomes a NAVIGATION
 * TARGET and web storage is attacker-writable under XSS — `sessionStorage` is
 * narrower in scope than `localStorage` but no harder to write from injected
 * script in the same tab — so the read is where the guard has to be. Control
 * characters are rejected because browsers strip TAB/LF/CR before parsing a URL,
 * which would let a validated string differ from the navigated one.
 */
const CONTROL_OR_WHITESPACE = /[\u0000-\u0020\u007F\s]/;

function isValidInvitePath(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (CONTROL_OR_WHITESPACE.test(value)) return false;
  if (!value.startsWith(INVITE_PATH_PREFIX)) return false;
  // A code must actually be present — the bare prefix is not a destination.
  return value.length > INVITE_PATH_PREFIX.length;
}

/** Remove the stored invite. Safe to call when nothing is stored. */
export function clearPendingInvite(): void {
  try {
    sessionStorage.removeItem(PENDING_INVITE_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/**
 * Remember an invite the visitor is about to authenticate in order to accept.
 * Silently no-ops on anything that is not an invite route.
 */
export function setPendingInvite(path: string): void {
  if (!isValidInvitePath(path)) return;
  try {
    sessionStorage.setItem(
      PENDING_INVITE_KEY,
      JSON.stringify({ path, ts: Date.now() } satisfies StoredPendingInvite),
    );
  } catch {
    /* storage unavailable — the invitee lands on the domain default instead */
  }
}

/**
 * Read the pending invite path without consuming it, or null when absent,
 * unreadable, malformed, or expired. Anything rejected is CLEARED so it cannot
 * be re-rejected on every subsequent login.
 */
export function peekPendingInvite(): string | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(PENDING_INVITE_KEY);
  } catch {
    return null;
  }

  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearPendingInvite();
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) {
    clearPendingInvite();
    return null;
  }

  const candidate = parsed as Record<string, unknown>;
  if (!isValidInvitePath(candidate.path) || typeof candidate.ts !== 'number') {
    clearPendingInvite();
    return null;
  }

  if (Date.now() - candidate.ts > TTL_MS) {
    clearPendingInvite();
    return null;
  }

  return candidate.path;
}
